import { maandVanIsoWeek } from '@/lib/locatieTypes';
import { isoWeekRange, isoWekenInJaar, isoWeekJaar } from '@/lib/locatieTypes';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getAccessToken } from '@/lib/tokenStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  TrendingUp, TrendingDown, Minus, AlertTriangle, Trophy,
  ArrowUpDown, ChevronUp, ChevronDown, Loader2, WifiOff,
  Euro, Target, Users, ShoppingBag, Calculator,
  Sparkles, RefreshCw, ChevronRight, X, CalendarDays, Send
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, ReferenceLine,
} from 'recharts';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getISOWeek(d) {
  const date = new Date(d.getTime());
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
  const week1 = new Date(date.getFullYear(), 0, 4);
  return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
}

// F3d/G1: isoWeekRange levert het complete label als string — niet destructureren.
// (4 januari 2026 valt op zondag, waardoor de oude berekening week 40 een week te
//  laat liet zien.)
const getWeekRange = (jaar, week) => isoWeekRange(jaar, week);

const MAAND_NAMEN_KORT = ['', 'jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const MAAND_NAMEN = ['', 'Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'];

const PERIODS = [
  { value: 'day', label: 'Dag' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Maand' },
  { value: 'quarter', label: 'Kwartaal' },
  { value: 'year', label: 'Jaar' },
];

const STATUS_CONFIG = {
  OK: { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  'LET OP': { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-200', dot: 'bg-amber-500' },
  HOOG: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200', dot: 'bg-red-500' },
  KRITIEK: { bg: 'bg-red-200', text: 'text-red-900', border: 'border-red-300', dot: 'bg-red-700' },
};

const formatCurrency = (v) => {
  if (v === 0) return '\u20AC 0';
  if (Math.abs(v) >= 1000000) return `\u20AC ${(v / 1000000).toFixed(2)}M`;
  if (Math.abs(v) >= 1000) return `\u20AC ${(v / 1000).toFixed(0)}K`;
  return `\u20AC ${v.toFixed(0)}`;
};

const formatNumber = (v) => new Intl.NumberFormat('nl-NL').format(Math.round(v));

const TrendIcon = ({ trend, className = 'w-4 h-4' }) => {
  if (trend === 'up') return <TrendingUp className={`${className} text-red-500`} />;
  if (trend === 'down') return <TrendingDown className={`${className} text-emerald-500`} />;
  return <Minus className={`${className} text-slate-400`} />;
};

const STATUS_TOOLTIP = {
  OK: 'Alles binnen target',
  'LET OP': 'PK% boven target of bijverkoop onder target',
  HOOG: 'PK% boven 40% — directe actie nodig',
  KRITIEK: 'PK% boven 45% — kritieke situatie',
};

const StatusBadge = ({ status }) => {
  const c = STATUS_CONFIG[status] || STATUS_CONFIG.OK;
  return (
    <span data-testid={`status-badge-${(status || '').toLowerCase().replace(/\s/g, '-')}`}
      title={STATUS_TOOLTIP[status] || ''}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${c.bg} ${c.text} ${c.border} border cursor-help`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {status}
    </span>
  );
};

const TargetIndicator = ({ value, target, type = 'pk' }) => {
  const isGood = type === 'pk' ? value <= target : value >= target;
  return <span className={`font-medium ${isGood ? 'text-emerald-600' : 'text-red-600'}`}>{value.toFixed(1)}%</span>;
};

const ProgressBar = ({ pct }) => {
  const clamped = Math.min(Math.max(pct, 0), 100);
  const color = clamped >= 90 ? 'bg-emerald-500' : clamped >= 75 ? 'bg-amber-500' : clamped >= 50 ? 'bg-orange-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${clamped}%` }} />
      </div>
      <span className="text-xs font-medium text-slate-600 w-10 text-right">{pct.toFixed(0)}%</span>
    </div>
  );
};

// ── AI Analyse Block ────────────────────────────────────────────────────────

const ANALYSE_SECTIONS = [
  { key: 'samenvatting', label: 'Samenvatting', color: 'border-l-tw-blue', icon: '1' },
  { key: 'probleemlocaties', label: 'Probleemlocaties', color: 'border-l-red-500', icon: '2' },
  { key: 'topperformers', label: 'Topperformers', color: 'border-l-emerald-500', icon: '3' },
  { key: 'trends', label: 'Trends', color: 'border-l-amber-500', icon: '4' },
  { key: 'actiepunten', label: 'Actiepunten', color: 'border-l-purple-500', icon: '5' },
];

function AIAnalyseSection({ period }) {
  const [analyse, setAnalyse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState({});

  const token = getAccessToken();
  const now = new Date();
  const params = new URLSearchParams({ period, jaar: now.getFullYear(), maand: now.getMonth() + 1 });

  const fetchCached = useCallback(async () => {
    try {
      const res = await fetch(`${API}/performance/analyse/latest?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.exists) setAnalyse(data);
    } catch { /* ignore */ }
  }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchCached(); }, [fetchCached]);

  const generate = async (force = false) => {
    setLoading(true);
    try {
      const url = `${API}/performance/analyse?${params}${force ? '&force=true' : ''}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Fout bij genereren analyse');
      const data = await res.json();
      setAnalyse(data);
      toast.success('AI analyse gegenereerd');
    } catch (err) {
      toast.error(err.message || 'Fout bij genereren');
    } finally {
      setLoading(false);
    }
  };

  const toggle = (key) => setCollapsed(p => ({ ...p, [key]: !p[key] }));

  const ai = analyse?.ai_analysis;

  return (
    <Card className="border-0 shadow-sm" data-testid="ai-analyse-section">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-tw-gold" />
            AI Analyse
          </CardTitle>
          <div className="flex items-center gap-2">
            {ai?.generated_at && (
              <span className="text-xs text-slate-400">
                Gegenereerd op {new Date(ai.generated_at).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            {analyse ? (
              <Button variant="outline" size="sm" onClick={() => generate(true)} disabled={loading} data-testid="ai-refresh-btn">
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
                Vernieuwen
              </Button>
            ) : (
              <Button size="sm" onClick={() => generate(false)} disabled={loading}
                className="bg-tw-blue hover:bg-tw-blue-light text-white" data-testid="ai-generate-btn">
                {loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                Genereer AI Analyse
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      {ai && (
        <CardContent className="pt-0 space-y-2">
          {ANALYSE_SECTIONS.map(({ key, label, color, icon }) => {
            const text = ai[key];
            if (!text) return null;
            const isOpen = !collapsed[key];
            return (
              <Collapsible key={key} open={isOpen} onOpenChange={() => toggle(key)}>
                <CollapsibleTrigger className={`w-full flex items-center justify-between p-3 rounded-lg border-l-4 ${color} bg-slate-50 hover:bg-slate-100 transition-colors`} data-testid={`analyse-section-${key}`}>
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-slate-200 text-[10px] font-bold flex items-center justify-center text-slate-600">{icon}</span>
                    <span className="text-sm font-semibold text-slate-700">{label}</span>
                  </div>
                  {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </CollapsibleTrigger>
                <CollapsibleContent className="px-3 pb-2 pt-1">
                  <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{text}</p>
                </CollapsibleContent>
              </Collapsible>
            );
          })}
        </CardContent>
      )}
      {loading && !analyse && (
        <CardContent className="pt-0">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-tw-blue mr-2" />
            <span className="text-sm text-slate-500">AI analyse wordt gegenereerd...</span>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

// ── Locatie Detail Sheet ────────────────────────────────────────────────────

function LocatieDetailSheet({ slug, open, onClose, period }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug || !open) return;
    setLoading(true);
    const token = getAccessToken();
    const now = new Date();
    const params = new URLSearchParams({ period, jaar: now.getFullYear(), maand: now.getMonth() + 1 });
    fetch(`${API}/performance/locatie/${slug}?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => setData(d))
      .catch(() => toast.error('Fout bij laden locatie'))
      .finally(() => setLoading(false));
  }, [slug, open, period]);

  const h = data?.huidig || {};
  const pkT = slug === 'rotterdam' ? 33 : 35;
  const bvT = slug === 'rotterdam' ? 8 : 7;

  // Chart data — last 6 months
  const chartData = useMemo(() => {
    if (!data?.historie) return [];
    return data.historie.slice(-6).map(p => ({
      name: p.periode,
      Omzet: Math.round(p.omzet / 1000),
      Target: Math.round(p.target / 1000),
      'PK%': p.pk_pct,
    }));
  }, [data]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white shadow-lg border border-slate-200 rounded-lg p-3 text-xs">
        <p className="font-semibold text-slate-700 mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }}>
            {p.name}: {p.name === 'PK%' ? `${p.value}%` : `\u20AC ${p.value}K`}
          </p>
        ))}
      </div>
    );
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto p-0" data-testid="locatie-detail-sheet">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-6 h-6 animate-spin text-tw-blue" />
          </div>
        ) : data ? (
          <div className="p-6 space-y-6">
            <SheetHeader className="space-y-1.5">
              <div className="flex items-center justify-between">
                <SheetTitle className="text-xl">{data.locatie_naam}</SheetTitle>
                <StatusBadge status={h.status || 'OK'} />
              </div>
              <p className="text-sm text-slate-500">{data.period_label}</p>
            </SheetHeader>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 gap-3" data-testid="detail-kpis">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500 mb-0.5">Omzet vs Target</p>
                <p className="text-lg font-bold">{formatCurrency(h.omzet || 0)}</p>
                <p className="text-xs text-slate-400" data-testid="performance-target-label">
                  {h.heeft_target === false ? 'Geen target ingesteld' : `van ${formatCurrency(h.target || 0)} (${h.voortgang_pct || 0}%)`}
                </p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500 mb-0.5">YTD Positie</p>
                <p className={`text-lg font-bold ${(data.ytd?.achterstand || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatCurrency(data.ytd?.achterstand || 0)}
                </p>
                <p className="text-xs text-slate-400">van {formatCurrency(data.ytd?.jaardoel || 0)}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500 mb-0.5">PK%</p>
                <p className={`text-lg font-bold ${(h.pk_pct || 0) <= pkT ? 'text-emerald-600' : 'text-red-600'}`}>
                  {h.pk_pct || 0}%
                </p>
                <p className="text-xs text-slate-400">Target: {pkT}%</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500 mb-0.5">Bijverkoop%</p>
                <p className={`text-lg font-bold ${(h.bijverkoop_pct || 0) >= bvT ? 'text-emerald-600' : 'text-red-600'}`}>
                  {h.bijverkoop_pct || 0}%
                </p>
                <p className="text-xs text-slate-400">Target: {bvT}%</p>
              </div>
            </div>

            {/* Charts */}
            {chartData.length > 0 && (
              <>
                <div data-testid="omzet-chart">
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Omzet trend (x1000)</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: -10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="Omzet" stroke="#1f2548" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="Target" stroke="#ffc315" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div data-testid="pk-chart">
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">PK% trend</h3>
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: -10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} domain={[0, 50]} />
                      <Tooltip content={<CustomTooltip />} />
                      <ReferenceLine y={pkT} stroke="#ef4444" strokeDasharray="3 3" label={{ value: `Target ${pkT}%`, fontSize: 10, fill: '#ef4444' }} />
                      <Line type="monotone" dataKey="PK%" stroke="#1f2548" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}

            {/* Historie Table */}
            {data.historie?.length > 0 && (
              <div data-testid="historie-tabel">
                <h3 className="text-sm font-semibold text-slate-700 mb-2">Historie</h3>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-50 border-b">
                        <th className="px-2 py-2 text-left font-semibold text-slate-500">Periode</th>
                        <th className="px-2 py-2 text-right font-semibold text-slate-500">Omzet</th>
                        <th className="px-2 py-2 text-right font-semibold text-slate-500">PK%</th>
                        <th className="px-2 py-2 text-right font-semibold text-slate-500">BV%</th>
                        <th className="px-2 py-2 text-center font-semibold text-slate-500">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.historie.map((h, i) => (
                        <tr key={i} className="border-b last:border-0 hover:bg-slate-50">
                          <td className="px-2 py-1.5 font-medium text-slate-700">{h.periode}</td>
                          <td className="px-2 py-1.5 text-right text-slate-600">{formatCurrency(h.omzet)}</td>
                          <td className="px-2 py-1.5 text-right">
                            <span className={h.pk_pct <= pkT ? 'text-emerald-600' : 'text-red-600'}>{h.pk_pct}%</span>
                          </td>
                          <td className="px-2 py-1.5 text-right">
                            <span className={h.bijverkoop_pct >= bvT ? 'text-emerald-600' : 'text-red-600'}>{h.bijverkoop_pct}%</span>
                          </td>
                          <td className="px-2 py-1.5 text-center"><StatusBadge status={h.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

// ── Main Page ───────────────────────────────────────────────────────────────

export default function PerformanceOverzicht() {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [sortConfig, setSortConfig] = useState({ key: 'omzet', direction: 'desc' });
  const [selectedLocatie, setSelectedLocatie] = useState(null);
  const [digestOpen, setDigestOpen] = useState(false);
  const [digestEmail, setDigestEmail] = useState('snoek19@gmail.com');
  const [digestSending, setDigestSending] = useState(false);

  // Period-specific selectors
  const now = new Date();
  const [selDate, setSelDate] = useState(now.toISOString().split('T')[0]); // day
  const [selWeek, setSelWeek] = useState(getISOWeek(now)); // week number
  const [selWeekYear, setSelWeekYear] = useState(isoWeekJaar(now));   // F3d: ISO-weekjaar
  const [selMonth, setSelMonth] = useState(now.getMonth() + 1);
  const [selMonthYear, setSelMonthYear] = useState(now.getFullYear());
  const [selQuarter, setSelQuarter] = useState(Math.ceil((now.getMonth() + 1) / 3));
  const [selQuarterYear, setSelQuarterYear] = useState(now.getFullYear());
  const [selYear, setSelYear] = useState(now.getFullYear());

  const sendTestDigest = async () => {
    setDigestSending(true);
    try {
      const token = getAccessToken();
      const res = await fetch(`${API}/performance/test-digest?test_email=${encodeURIComponent(digestEmail)}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Versturen mislukt');
      const d = await res.json();
      toast.success(`Digest verstuurd naar ${digestEmail} (MT: ${d.result?.mt_recipients}, BL: ${d.result?.bl_sent})`);
      setDigestOpen(false);
    } catch (err) {
      toast.error(err.message || 'Fout bij versturen');
    } finally {
      setDigestSending(false);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = getAccessToken();
      const params = new URLSearchParams({ period });

      if (period === 'day') {
        const d = new Date(selDate);
        params.set('jaar', d.getFullYear());
        params.set('maand', d.getMonth() + 1);
        params.set('dag', d.getDate());
      } else if (period === 'week') {
        params.set('jaar', selWeekYear);
        params.set('week', selWeek);
        params.set('maand', maandVanIsoWeek(selWeekYear, selWeek));
      } else if (period === 'month') {
        params.set('jaar', selMonthYear);
        params.set('maand', selMonth);
      } else if (period === 'quarter') {
        const qStartMonth = (selQuarter - 1) * 3 + 1;
        params.set('jaar', selQuarterYear);
        params.set('maand', qStartMonth);
      } else if (period === 'year') {
        params.set('jaar', selYear);
      }

      const res = await fetch(`${API}/performance/overview?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Fout bij ophalen data');
      setData(await res.json());
    } catch (err) {
      toast.error('Fout bij laden performance data');
    } finally {
      setLoading(false);
    }
  }, [period, selDate, selWeek, selWeekYear, selMonth, selMonthYear, selQuarter, selQuarterYear, selYear]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sortedLocaties = useMemo(() => {
    if (!data?.per_locatie) return [];
    return [...data.per_locatie].sort((a, b) => {
      const aVal = a[sortConfig.key] ?? 0;
      const bVal = b[sortConfig.key] ?? 0;
      if (typeof aVal === 'string') return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [data, sortConfig]);

  const probleemLocaties = useMemo(() => {
    if (!data?.per_locatie) return [];
    return data.per_locatie.filter(l => l.status === 'HOOG' || l.status === 'KRITIEK' || (l.trend_pk === 'up' && l.pk_pct > 37));
  }, [data]);

  const topPerformers = useMemo(() => {
    if (!data?.per_locatie) return [];
    return data.per_locatie.filter(l => l.status === 'OK' && l.voortgang_pct >= 95);
  }, [data]);

  const handleSort = (key) => setSortConfig(p => ({ key, direction: p.key === key && p.direction === 'desc' ? 'asc' : 'desc' }));

  const SortHeader = ({ label, sortKey, className = '' }) => (
    <th className={`px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-tw-blue select-none ${className}`} onClick={() => handleSort(sortKey)}>
      <div className="flex items-center gap-1">
        {label}
        {sortConfig.key === sortKey ? (sortConfig.direction === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-30" />}
      </div>
    </th>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="performance-loading">
        <Loader2 className="w-8 h-8 animate-spin text-tw-blue" />
        <span className="ml-3 text-slate-500">Performance data laden...</span>
      </div>
    );
  }

  if (!data) return null;
  const t = data.totals;
  const pkTarget = 35;
  const bvTarget = 7;

  return (
    <div className="space-y-6" data-testid="performance-overview">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="performance-title">Performance</h1>
          <p className="text-sm text-slate-500 mt-0.5">{data.period_label}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {isAdmin() && (
            <Button variant="outline" size="sm" onClick={() => setDigestOpen(true)} data-testid="test-digest-btn" className="text-slate-500 hover:text-tw-blue">
              <Send className="w-3.5 h-3.5 mr-1.5" />Test digest
            </Button>
          )}
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1" data-testid="period-selector">
          {PERIODS.map(p => (
            <Button key={p.value} variant={period === p.value ? 'default' : 'ghost'} size="sm" onClick={() => setPeriod(p.value)} data-testid={`period-${p.value}`}
              className={period === p.value ? 'bg-tw-blue text-white hover:bg-tw-blue-light shadow-sm' : 'text-slate-600 hover:text-slate-900'}>
              {p.label}
            </Button>
          ))}
          </div>
        </div>
      </div>

      {/* Period-specific date picker */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="period-date-picker">
        {period === 'day' && (
          <div className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-slate-400" />
            <Input type="date" value={selDate} onChange={e => setSelDate(e.target.value)}
              className="w-44 h-8 text-sm" data-testid="day-picker" />
          </div>
        )}
        {period === 'week' && (
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-2 py-1">
            <button onClick={() => { let w = selWeek - 1, y = selWeekYear; if (w < 1) { y--; w = isoWekenInJaar(y); } setSelWeek(w); setSelWeekYear(y); }}
              className="p-1 hover:bg-slate-100 rounded" data-testid="week-prev"><ChevronDown className="w-3.5 h-3.5 rotate-90" /></button>
            <span className="px-2 text-sm font-medium text-slate-700 whitespace-nowrap" data-testid="week-label">
              Week {selWeek} ({getWeekRange(selWeekYear, selWeek)})
            </span>
            <button onClick={() => { let w = selWeek + 1, y = selWeekYear; if (w > isoWekenInJaar(y)) { y++; w = 1; } setSelWeek(w); setSelWeekYear(y); }}
              className="p-1 hover:bg-slate-100 rounded" data-testid="week-next"><ChevronDown className="w-3.5 h-3.5 -rotate-90" /></button>
          </div>
        )}
        {period === 'month' && (
          <div className="flex items-center gap-2">
            <select value={selMonth} onChange={e => setSelMonth(parseInt(e.target.value))}
              className="h-8 text-sm border border-slate-200 rounded-md px-2 bg-white" data-testid="month-picker">
              {MAAND_NAMEN.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
            </select>
            <select value={selMonthYear} onChange={e => setSelMonthYear(parseInt(e.target.value))}
              className="h-8 text-sm border border-slate-200 rounded-md px-2 bg-white" data-testid="month-year-picker">
              {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        )}
        {period === 'quarter' && (
          <div className="flex items-center gap-2">
            <div className="flex gap-1 bg-white border border-slate-200 rounded-lg p-0.5">
              {[1,2,3,4].map(q => (
                <button key={q} onClick={() => setSelQuarter(q)}
                  className={`px-3 py-1 text-sm rounded-md font-medium transition-colors ${selQuarter === q ? 'bg-tw-blue text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                  data-testid={`quarter-q${q}`}>Q{q}</button>
              ))}
            </div>
            <select value={selQuarterYear} onChange={e => setSelQuarterYear(parseInt(e.target.value))}
              className="h-8 text-sm border border-slate-200 rounded-md px-2 bg-white" data-testid="quarter-year-picker">
              {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        )}
        {period === 'year' && (
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-2 py-1">
            <button onClick={() => setSelYear(y => y - 1)} className="p-1 hover:bg-slate-100 rounded" data-testid="year-prev"><ChevronDown className="w-3.5 h-3.5 rotate-90" /></button>
            <span className="px-3 text-sm font-semibold text-slate-700" data-testid="year-label">{selYear}</span>
            <button onClick={() => setSelYear(y => y + 1)} className="p-1 hover:bg-slate-100 rounded" disabled={selYear >= now.getFullYear()} data-testid="year-next"><ChevronDown className="w-3.5 h-3.5 -rotate-90" /></button>
          </div>
        )}
      </div>

      {/* DailyOps warning */}
      {data.dailyops_connected === false && (
        <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="dailyops-warning">
          <WifiOff className="w-5 h-5 text-amber-600 shrink-0" />
          <p className="text-sm text-amber-800">DailyOps database is momenteel niet bereikbaar. Targets worden getoond, maar omzetdata is niet beschikbaar.</p>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="kpi-cards">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 bg-tw-blue/10 rounded-md"><Euro className="w-4 h-4 text-tw-blue" /></div>
              <span className="text-xs font-medium text-slate-500 uppercase">Omzet</span>
            </div>
            <p className="text-xl font-bold text-slate-900" data-testid="kpi-omzet">{formatCurrency(t.omzet)}</p>
            <div className="flex items-center gap-1.5 mt-1">
              <span className="text-xs text-slate-400">Target: {formatCurrency(t.target)}</span>
              <span className={`text-xs font-semibold ${t.verschil >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{t.verschil >= 0 ? '+' : ''}{t.verschil_pct}%</span>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 bg-tw-blue/10 rounded-md"><Target className="w-4 h-4 text-tw-blue" /></div>
              <span className="text-xs font-medium text-slate-500 uppercase">YTD</span>
            </div>
            <p className="text-xl font-bold text-slate-900" data-testid="kpi-ytd">{formatCurrency(t.omzet_ytd)}</p>
            <div className="flex items-center gap-1.5 mt-1">
              <span className={`text-xs font-semibold ${t.achterstand_ytd >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{t.achterstand_ytd >= 0 ? '+' : ''}{formatCurrency(t.achterstand_ytd)}</span>
              <span className="text-xs text-slate-400">vs schema</span>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 bg-tw-blue/10 rounded-md"><Users className="w-4 h-4 text-tw-blue" /></div>
              <span className="text-xs font-medium text-slate-500 uppercase">PK%</span>
            </div>
            <p className={`text-xl font-bold ${t.pk_pct_gewogen <= pkTarget ? 'text-emerald-600' : 'text-red-600'}`} data-testid="kpi-pk">{t.pk_pct_gewogen}%</p>
            <span className="text-xs text-slate-400">Target: {pkTarget}%</span>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 bg-tw-blue/10 rounded-md"><ShoppingBag className="w-4 h-4 text-tw-blue" /></div>
              <span className="text-xs font-medium text-slate-500 uppercase">Bijverkoop</span>
            </div>
            <p className={`text-xl font-bold ${t.bijverkoop_pct >= bvTarget ? 'text-emerald-600' : 'text-red-600'}`} data-testid="kpi-bv">{t.bijverkoop_pct}%</p>
            <span className="text-xs text-slate-400">Target: {bvTarget}%</span>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 bg-tw-blue/10 rounded-md"><Calculator className="w-4 h-4 text-tw-blue" /></div>
              <span className="text-xs font-medium text-slate-500 uppercase">Nodig/mnd</span>
            </div>
            <p className="text-xl font-bold text-slate-900" data-testid="kpi-nodig">{formatCurrency(t.benodigde_maandomzet)}</p>
            <span className="text-xs text-slate-400">rest van {data.jaar}</span>
          </CardContent>
        </Card>
      </div>

      {/* Probleemlocaties */}
      {probleemLocaties.length > 0 && (
        <Card className="border-0 shadow-sm border-l-4 border-l-red-500" data-testid="probleem-locaties">
          <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-500" />Aandachtspunten ({probleemLocaties.length})</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <div className="flex flex-wrap gap-2">
              {probleemLocaties.map(l => (
                <div key={l.locatie} className="flex items-center gap-2 px-3 py-2 bg-red-50 rounded-lg border border-red-100 cursor-pointer hover:bg-red-100 transition-colors" onClick={() => setSelectedLocatie(l.locatie)}>
                  <StatusBadge status={l.status} /><span className="text-sm font-medium text-slate-700">{l.locatie_naam}</span><span className="text-xs text-slate-500">PK: {l.pk_pct}%</span><TrendIcon trend={l.trend_pk} className="w-3.5 h-3.5" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top performers */}
      {topPerformers.length > 0 && (
        <Card className="border-0 shadow-sm border-l-4 border-l-emerald-500" data-testid="top-performers">
          <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Trophy className="w-4 h-4 text-emerald-500" />Topperformers ({topPerformers.length})</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <div className="flex flex-wrap gap-2">
              {topPerformers.map(l => (
                <div key={l.locatie} className="flex items-center gap-2 px-3 py-2 bg-emerald-50 rounded-lg border border-emerald-100 cursor-pointer hover:bg-emerald-100 transition-colors" onClick={() => setSelectedLocatie(l.locatie)}>
                  <span className="text-sm font-medium text-slate-700">{l.locatie_naam}</span><span className="text-xs text-emerald-600 font-semibold">{l.voortgang_pct}%</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Locatie Tabel */}
      <Card className="border-0 shadow-sm" data-testid="locatie-tabel">
        <CardHeader className="pb-3"><CardTitle className="text-base">Alle locaties</CardTitle></CardHeader>
        <CardContent className="pt-0">
          <div className="overflow-x-auto -mx-6">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="border-b border-slate-100">
                  <SortHeader label="Locatie" sortKey="locatie_naam" className="pl-6" />
                  <SortHeader label="Omzet" sortKey="omzet" />
                  <SortHeader label="Target" sortKey="target" />
                  <SortHeader label="Voortgang" sortKey="voortgang_pct" className="w-40" />
                  <SortHeader label="PK%" sortKey="pk_pct" />
                  <SortHeader label="BV%" sortKey="bijverkoop_pct" />
                  <SortHeader label="Wassingen" sortKey="wassingen" />
                  <SortHeader label="Status" sortKey="status" className="pr-6" />
                </tr>
              </thead>
              <tbody>
                {sortedLocaties.map((loc, i) => {
                  const pkT = loc.locatie === 'rotterdam' ? 33 : 35;
                  const bvT = loc.locatie === 'rotterdam' ? 8 : 7;
                  return (
                    <tr key={loc.locatie} data-testid={`locatie-row-${loc.locatie}`}
                      className={`border-b border-slate-50 hover:bg-slate-50/80 transition-colors cursor-pointer ${i % 2 === 0 ? '' : 'bg-slate-25'}`}
                      onClick={() => setSelectedLocatie(loc.locatie)}>
                      <td className="px-3 py-3 pl-6">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm text-slate-900">{loc.locatie_naam}</span>
                          <TrendIcon trend={loc.trend_omzet} className="w-3.5 h-3.5" />
                          <ChevronRight className="w-3.5 h-3.5 text-slate-300 ml-auto" />
                        </div>
                      </td>
                      <td className="px-3 py-3 text-sm font-medium text-slate-700">{formatCurrency(loc.omzet)}</td>
                      <td className="px-3 py-3 text-sm text-slate-500">{loc.heeft_target === false ? 'geen target' : formatCurrency(loc.target)}</td>
                      <td className="px-3 py-3"><ProgressBar pct={loc.voortgang_pct} /></td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1.5"><TargetIndicator value={loc.pk_pct} target={pkT} type="pk" /><TrendIcon trend={loc.trend_pk} className="w-3 h-3" /></div>
                      </td>
                      <td className="px-3 py-3"><TargetIndicator value={loc.bijverkoop_pct} target={bvT} type="bv" /></td>
                      <td className="px-3 py-3 text-sm text-slate-600">{formatNumber(loc.wassingen)}</td>
                      <td className="px-3 py-3 pr-6"><StatusBadge status={loc.status} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* AI Analyse */}
      <AIAnalyseSection period={period} />

      {/* Detail Sheet */}
      <LocatieDetailSheet slug={selectedLocatie} open={!!selectedLocatie} onClose={() => setSelectedLocatie(null)} period={period} />

      {/* Test Digest Modal */}
      <Dialog open={digestOpen} onOpenChange={setDigestOpen}>
        <DialogContent className="max-w-sm" data-testid="test-digest-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Send className="w-4 h-4" />Test digest versturen</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Email adres</label>
            <Input value={digestEmail} onChange={e => setDigestEmail(e.target.value)} placeholder="email@voorbeeld.nl" type="email" data-testid="test-digest-email" />
            <p className="text-xs text-slate-400 mt-1.5">Stuurt 1 MT digest + 1 BL digest naar dit adres.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setDigestOpen(false)}>Annuleren</Button>
            <Button size="sm" onClick={sendTestDigest} disabled={digestSending || !digestEmail} className="bg-tw-blue hover:bg-tw-blue-light text-white" data-testid="test-digest-send-btn">
              {digestSending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
              Verstuur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
